<template>
  <section
    :class="{ 'split-apart': splitApart, 'new-version': newVersion }"
    class="summary-container"
    :style="`width: ${width}; height: ${height}`"
    aria-label="Metrics summary"
  >
    <div
      v-for="({
        id, color, label, value, tooltip, comingSoon, link, summaryLabelWidth,
      }, i) in summaries"
      :key="label"
      :data-widget-id="id"
      :class="{
        'summary-section': true,
        'coming-soon-section': comingSoon,
        'summary-divider': i !== summaries.length - 1 && summaries.length > 1,
      }"
    >
      <div v-if="!comingSoon" class="summary-section-content">
        <div class="top-right-tooltip d-flex align-center ga-2">
          <ChartDownload
            v-if="showDownload"
            :download-id="id"
            :file-name="downloadPngFileName(id)"
          />
          <InsightsTooltip
            v-if="tooltip?.key && link?.to"
            :tooltip="tooltip"
            max-width="360px"
            teleport
          />
        </div>
        <p
          :style="`color: ${label === 'Average Score' ? getColor(value) : color};`"
          class="number-display mb-1"
        >
          {{ value }}
        </p>
        <p
          v-if="!Array.isArray(label)"
          class="summary-label"
          :style="`max-width: ${summaryLabelWidth || labelWidth};`"
        >
          {{ label }}
        </p>
        <div v-else>
          <p
            v-for="labelPart in label"
            :key="labelPart"
            class="summary-label"
            :style="`max-width: ${summaryLabelWidth || labelWidth};`"
          >
            {{ labelPart }}
          </p>
        </div>
      </div>
      <div v-else class="coming-soon-content">
        <p class="text-h2 mb-4">
          {{ label }}
        </p>
        <p class="coming-soon-description">
          COMING SOON
        </p>
      </div>
      <InsightsTooltip
        v-if="tooltip?.key && !link?.to"
        :tooltip="tooltip"
        class="bottom-right-tooltip"
        max-width="360px"
        teleport
      />
      <ChartLink :link="link" />
    </div>
  </section>
</template>

<script>
import InsightsTooltip from '@/components/insights/InsightsTooltip.vue';
import { colorMap } from '@/helpers/formatters';
import { useColors } from '@/composables/colors';
import ChartLink from '@/components/insights/Chart/ChartLink.vue';
import ChartDownload from '@/components/insights/Chart/ChartDownload.vue';

export default {
  name: 'MetricsSummary',
  components: {
    InsightsTooltip,
    ChartLink,
    ChartDownload,
  },
  props: {
    summaries: {
      type: Array,
      default() {
        return [];
      },
    },
    height: {
      type: String,
      default: '140px',
    },
    width: {
      type: String,
      default: '100%',
    },
    splitApart: {
      type: Boolean,
      default: false,
    },
    labelWidth: {
      type: String,
      default: '105px',
    },
    showDownload: {
      type: Boolean,
      default: false,
    },
    downloadPngFileName: {
      type: Function,
      default: () => '',
    },
    newVersion: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const {
      brandPrimary,
      grey400,
      basicRed,
      orange500,
      blue500,
      green500,
    } = useColors();

    return {
      brandPrimary,
      colorsToMap: {
        grey400,
        basicRed,
        orange500,
        blue500,
        green500,
      },
    };
  },
  methods: {
    getColor(score) {
      if (score === 0 || score === '0') {
        return this.brandPrimary;
      }
      return colorMap(score, this.colorsToMap);
    },
  },
};
</script>

<style lang="less" scoped>
@import '@/less/mixins';
.summary-container {
  background: @white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 10px 20px fade(@bg-brand-default, 5%);

  .summary-section {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    height: 100%;
    padding: 24px 8px 8px 24px;
    position: relative;
  }

  .summary-section-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    height: 100%;
  }

  .summary-divider {
    border-right: 1px solid @stroke-default;
  }

  .number-display {
    font-size: 36px;
    line-height: 48px;
    font-weight: 500;
  }

  &.split-apart {
    background: transparent;
    box-shadow: none;
    gap: 12px;

    .summary-section {
      background: @white;
      border-radius: 10px;
      .shadow();
    }

    .summary-divider {
      border: none;
    }

      &.new-version {
        border-radius: 8px;

        .summary-section {
          border-radius: 8px;
          border: 1px solid @stroke-default;
          box-shadow: none;
          padding: 16px;
        }
      }
  }

  &:not(.split-apart).new-version {
    border-radius: 8px;
    border: 1px solid @stroke-default;
    box-shadow: none;

    .summary-section {
      border: none;
      padding: 16px;
    }

    .summary-section:not(:last-child) {
      border-right: 1px solid @stroke-default;
    }
  }
}

.summary-section {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  padding: 24px;
}

.summary-divider {
  border-right: 1px solid @stroke-default;
}

.summary-label {
  max-width: 105px;
  text-align: left;
}

.coming-soon-section {
  padding: 24px !important;
  background: @bg-input !important;

  .coming-soon-content {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    width: 100%;
    height: 100%;

    .coming-soon-description {
      font-size: 12px !important;
      font-weight: 400;
      color: @text-brand;
    }
  }
}

.top-right-tooltip {
  position: absolute;
  top: 22px;
  right: 24px;
}

.new-version .top-right-tooltip {
  top: 14px;
  right: 16px;
}

.bottom-right-tooltip {
  position: absolute;
  bottom: 5px;
  right: 5px;
}
</style>
